/**
 * Apps2Go Hub – Per-moduuli henkilökunta-näkymä (Phase 17B)
 *
 * Geneerinen komponentti joka voidaan upottaa MIHIN TAHANSA moduuliin:
 *
 *   import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
 *   <ModuleStaffTab moduleKey="restaurant" moduleLabel="Ravintola" />
 *
 * Näyttää listan henkilökunnasta joilla on PÄÄSY tähän moduuliin
 * (permissions[moduleKey].access_level !== "none"). Klikkaus avaa täyden
 * henkilöstökortin uudessa välilehdessä.
 *
 * Tarjoaa myös nopean access-tason vaihdon ilman että käyttäjän tarvitsee
 * avata koko henkilöstökorttia.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ExternalLink, Loader2, Search, ShieldCheck, ShieldOff, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ACCESS_BADGES = {
  none:    { label: "Ei oikeutta",     bg: "bg-white/5",        color: "text-white/40" },
  view:    { label: "Vain katselu",    bg: "bg-cyan-500/15",    color: "text-cyan-300" },
  partial: { label: "Osittainen",      bg: "bg-amber-500/15",   color: "text-amber-300" },
  full:    { label: "Täydet oikeudet", bg: "bg-emerald-500/15", color: "text-emerald-300" },
};
const LEVEL_ORDER = ["none", "view", "partial", "full"];

export default function ModuleStaffTab({ moduleKey, moduleLabel }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // 'all' | 'access' | 'noaccess'
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const isManager = user?.role === "admin" || user?.role === "license_customer";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Tarvitsemme FULL profiilit nähdäksemme permissions-kentät.
      // Käytetään /admin/staff (lyhennetty), sitten haetaan permissions-lähtöinen
      // näkymä erikseen jokaiselle henkilölle? → liian raskas.
      // Vaihtoehto: laajenna /admin/staff palauttamaan permissions[moduleKey] suoraan.
      const list = await api.get("/admin/staff");
      // Hae permissions yksitellen (paralleeli) – pidetään yksinkertaisena
      const withPerms = await Promise.all(
        (list.data || []).map(async (u) => {
          try {
            const p = await api.get(`/admin/staff/${u.id}`);
            return { ...u, permissions: p.data.permissions || {} };
          } catch {
            return { ...u, permissions: {} };
          }
        })
      );
      setAllStaff(withPerms);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isManager) load();
    else setLoading(false);
  }, [load, isManager]);

  const updateAccess = async (staff, newLevel) => {
    setUpdatingId(staff.id);
    try {
      const currentPerms = staff.permissions || {};
      const newPerms = {
        ...currentPerms,
        [moduleKey]: {
          ...(currentPerms[moduleKey] || {}),
          access_level: newLevel,
        },
      };
      // Jos vaihdetaan partial → tyhjennetään actions jos sieltä puuttuu kentät
      if (newLevel !== "partial") {
        newPerms[moduleKey].actions = newPerms[moduleKey].actions || [];
      }
      const r = await api.patch(`/admin/staff/${staff.id}`, { permissions: newPerms });
      setAllStaff((prev) => prev.map((s) => (s.id === staff.id ? { ...s, permissions: r.data.permissions || newPerms } : s)));
      toast.success(`${staff.full_name}: ${ACCESS_BADGES[newLevel].label}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setUpdatingId(null);
  };

  if (!isManager) {
    return (
      <div className="glass-card p-8 text-center" data-testid="module-staff-no-access">
        <ShieldOff className="w-10 h-10 mx-auto text-white/30 mb-2" />
        <p className="text-sm text-white/50">Vain ylläpitäjä tai lisenssiomistaja saa hallita henkilökuntaa.</p>
      </div>
    );
  }

  const lowerSearch = search.trim().toLowerCase();
  const filtered = allStaff.filter((s) => {
    const lvl = s.permissions?.[moduleKey]?.access_level || "none";
    if (filter === "access" && lvl === "none") return false;
    if (filter === "noaccess" && lvl !== "none") return false;
    if (lowerSearch) {
      const hay = `${s.full_name} ${s.email} ${s.position || ""} ${s.department || ""}`.toLowerCase();
      if (!hay.includes(lowerSearch)) return false;
    }
    return true;
  });

  const grantedCount = allStaff.filter(s => (s.permissions?.[moduleKey]?.access_level || "none") !== "none").length;

  return (
    <div className="space-y-4" data-testid={`module-staff-${moduleKey}`}>
      {/* Header + filtterit */}
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-amber-300/80">{moduleLabel}-moduulin henkilökunta</p>
          <p className="text-sm text-white/70">
            {grantedCount} / {allStaff.length} henkilöllä on pääsy
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex gap-1" data-testid="module-staff-filters">
            {[
              { v: "", label: "Kaikki" },
              { v: "access", label: "Pääsy" },
              { v: "noaccess", label: "Ei pääsyä" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`px-3 py-1 rounded-full text-xs border ${
                  filter === f.v ? "bg-amber-500/15 border-amber-400/40 text-amber-200" : "border-white/10 text-white/60"
                }`}
                data-testid={`module-staff-filter-${f.v || "all"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Etsi…"
              className="pl-7 h-8 w-44 text-xs"
              data-testid="module-staff-search"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/admin/users")}
            className="border-cyan-400/30 text-cyan-200 inline-flex items-center gap-1"
            data-testid="module-staff-add-user"
          >
            <UserPlus className="w-3.5 h-3.5" /> Lisää käyttäjä
          </Button>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-8 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-8 text-center text-sm text-white/40">
          {allStaff.length === 0 ? "Ei vielä henkilökuntaa – luo käyttäjä admin-näkymässä." : "Ei osumia suodattimella."}
        </div>
      ) : (
        <div className="glass-card p-0 overflow-hidden" data-testid="module-staff-list">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-2">Henkilö</th>
                <th className="text-left px-4 py-2">Rooli {moduleLabel}-moduulissa</th>
                <th className="text-right px-4 py-2">Pääsy</th>
                <th className="text-right px-4 py-2">Vaihda nopeasti</th>
                <th className="text-right px-4 py-2">Avaa</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const perm = s.permissions?.[moduleKey] || { access_level: "none" };
                const lvl = perm.access_level || "none";
                const badge = ACCESS_BADGES[lvl];
                return (
                  <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`module-staff-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{s.full_name}</p>
                      <p className="text-[10px] text-white/40">
                        {s.email}
                        {s.position && ` · ${s.position}`}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white/80 text-xs">{perm.role || <span className="text-white/30 italic">—</span>}</p>
                      {perm.scope && lvl !== "none" && (
                        <p className="text-[10px] text-white/40">
                          Näkymä: {perm.scope === "own" ? "vain omat" : perm.scope === "team" ? "tiimin" : "kaikki"}
                          {lvl === "partial" && perm.actions?.length > 0 && ` · ${perm.actions.length} toimintoa`}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Badge className={`${badge.bg} ${badge.color} text-[10px]`} data-testid={`module-staff-level-${s.id}`}>
                        {badge.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1" data-testid={`module-staff-quickset-${s.id}`}>
                        {LEVEL_ORDER.map((l) => (
                          <button
                            key={l}
                            onClick={() => updateAccess(s, l)}
                            disabled={updatingId === s.id || lvl === l}
                            className={`px-2 py-0.5 rounded text-[10px] border transition ${
                              lvl === l
                                ? `${ACCESS_BADGES[l].bg} border-white/20 ${ACCESS_BADGES[l].color} font-medium`
                                : "border-white/10 text-white/40 hover:text-white"
                            } ${updatingId === s.id ? "opacity-50" : ""}`}
                            data-testid={`module-staff-set-${s.id}-${l}`}
                            title={ACCESS_BADGES[l].label}
                          >
                            {l === "none" ? "✕" : l === "view" ? "👁" : l === "partial" ? "◐" : "✓"}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/admin/staff/${s.id}`)}
                        className="text-cyan-300"
                        data-testid={`module-staff-open-${s.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Help-banneri */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-3 flex gap-2 items-start">
        <ShieldCheck className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-white/70">
          <strong className="text-emerald-200">Vinkki:</strong> Pikatasovaihto vaihtaa kun käyttäjällä on pääsy tähän moduuliin.
          Yksityiskohtaiset oikeudet (sallitut toiminnot, näkymä, rooli) muokkaat avaamalla henkilökortin → välilehti
          &quot;Oikeudet&quot;.
        </p>
      </div>
    </div>
  );
}
